import { useEffect, useRef } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import { getSelectors } from "Reducers/activity";
import { useAppSelector } from "Store/hooks";

import type { Context } from "Reducers/activity";

type Props = {
  watchForActivityTypes: string | string[];
  onCompletion?: () => Promise<void> | void;
  context: Context;
  organizationId?: string;
  projectId?: string;
  environmentId?: string;
};

export const useActivityListener = ({
  watchForActivityTypes,
  onCompletion = () => undefined,
  context,
  organizationId,
  projectId,
  environmentId
}: Props) => {
  const {
    organizationId: orgId,
    projectId: projId,
    environmentId: envId
  } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId?: string;
  }>();

  const readyToDispatch = useRef(false);

  const { inProgress, completed } = getSelectors(context, {
    organizationId: organizationId ?? orgId,
    projectId: projectId ?? projId,
    environmentId: environmentId ?? envId
  });

  const inProgressActivity = useAppSelector(inProgress);
  const completedActivity = useAppSelector(completed);

  const activities =
    watchForActivityTypes instanceof Array
      ? watchForActivityTypes
      : [watchForActivityTypes];

  const hasInProgressActivities = inProgressActivity.some(activity =>
    activities.includes(activity.type)
  );
  const hasCompletedActivities = completedActivity.some(activity =>
    activities.includes(activity.type)
  );

  useEffect(() => {
    if (!readyToDispatch.current && hasInProgressActivities) {
      readyToDispatch.current = true;
    }
  }, [hasInProgressActivities]);

  useEffect(() => {
    if (
      readyToDispatch.current &&
      hasCompletedActivities &&
      !hasInProgressActivities
    ) {
      readyToDispatch.current = false;
      onCompletion();
    }
  }, [hasCompletedActivities, hasInProgressActivities, onCompletion]);

  return readyToDispatch.current;
};
